import { Injectable } from "@angular/core";
import { LOCALSTORAGEKEYS } from "../../_constants/localStorageKeys";
import { LocalService } from "../_storage/local.service";

export interface ModulePermission {
  view: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
  workflowPermission: string | null;
}

@Injectable({ providedIn: "root" })
export class ModulePermissionService {
  constructor(private localService: LocalService) {}

  private permissionsMap(): Record<string, ModulePermission> {
    const stored = this.localService.get(LOCALSTORAGEKEYS.MODULEPERMISSIONS);
    const map = stored?.permissions ?? stored ?? {};
    return map && typeof map === "object" ? map : {};
  }

  get(moduleKey: string): ModulePermission {
    const entry = this.permissionsMap()[moduleKey] || {};
    return {
      view: entry.view === true,
      create: entry.create === true,
      update: entry.update === true,
      delete: entry.delete === true,
      workflowPermission: this.normalizeWorkflow(entry.workflowPermission),
    };
  }

  private normalizeWorkflow(raw: any): string | null {
    if (typeof raw !== "string") {
      return null;
    }
    const value = raw.trim().toLowerCase();
    if (!value || value === "null" || value === "undefined" || value === "none") {
      return null;
    }
    return value;
  }

  resolveKey(moduleKeys: string[]): string | null {
    return moduleKeys.find((key) => this.has(key)) ?? null;
  }

  effective(moduleKeys: string[]): ModulePermission & { resolvedKey: string | null } {
    const key = this.resolveKey(moduleKeys);
    if (!key) {
      return {
        view: false,
        create: false,
        update: false,
        delete: false,
        workflowPermission: null,
        resolvedKey: null,
      };
    }
    return { ...this.get(key), resolvedKey: key };
  }

  canEditModule(moduleKeys: string[]): boolean {
    const perm = this.effective(moduleKeys);
    return perm.create || perm.update;
  }

  canView(moduleKey: string): boolean {
    return this.get(moduleKey).view;
  }

  canCreate(moduleKey: string): boolean {
    return this.get(moduleKey).create;
  }

  canUpdate(moduleKey: string): boolean {
    return this.get(moduleKey).update;
  }

  canEdit(moduleKey: string): boolean {
    const entry = this.get(moduleKey);
    return entry.create || entry.update;
  }

  canDelete(moduleKey: string): boolean {
    return this.get(moduleKey).delete;
  }

  canApprove(moduleKey: string): boolean {
    return this.get(moduleKey).workflowPermission === "approver";
  }

  canRevoke(moduleKey: string): boolean {
    return this.get(moduleKey).workflowPermission === "revoke";
  }

  isApprover(moduleKeys: string[]): boolean {
    return this.effective(moduleKeys).workflowPermission === "approver";
  }

  isRevoker(moduleKeys: string[]): boolean {
    return this.effective(moduleKeys).workflowPermission === "revoke";
  }

  has(moduleKey: string): boolean {
    return moduleKey in this.permissionsMap();
  }

  workflow(moduleKey: string): string | null {
    return this.get(moduleKey).workflowPermission;
  }
}
